import { useCallback, useEffect, useState } from 'react';
import { t } from '../i18n';
import { isRetryableLoadError } from '../lib/loadErrors';
import {
  api,
  ApiError,
  type AuditEntry,
  type CreatedEnrollmentToken,
  type EnrollmentTokenRow,
  type FamilyDetail,
  type FamilyInviteRow,
  type FamilyRole,
} from '../lib/api';

export type CreateEnrollmentInput = {
  kind: 'enroll';
  // No handle: the new person picks theirs when they open the link (D348).
  displayName: string;
  familyRole?: FamilyRole;
};

// In-app family administration (identity spec §7.3): members, personal
// links for new people (never a device: D334), invites, audit log.
export function useFamilyAdmin(familyId: string | undefined) {
  const [family, setFamily] = useState<FamilyDetail | null>(null);
  const [tokens, setTokens] = useState<EnrollmentTokenRow[]>([]);
  const [invites, setInvites] = useState<FamilyInviteRow[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A load the server couldn't answer, for Try again (M66, D249); a 403 or
  // 404 is in `error` only, with the page's own message and way back.
  const [failure, setFailure] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    if (!familyId) return;
    setLoading(true);
    const [familyRes, tokensRes, invitesRes, auditRes] =
      await Promise.allSettled([
        api.getFamily(familyId),
        api.getFamilyEnrollmentTokens(familyId),
        api.getFamilyInvites(familyId),
        api.getFamilyAuditLog(familyId, 100),
      ]);

    if (familyRes.status === 'fulfilled') {
      setFamily(familyRes.value.data);
      setError(null);
      setFailure(null);
    } else {
      const err = familyRes.reason;
      setFailure(isRetryableLoadError(err) ? err : null);
      setError(
        err instanceof ApiError ? err.message : t('account.family.loadFailed')
      );
    }
    if (tokensRes.status === 'fulfilled') setTokens(tokensRes.value.data);
    if (invitesRes.status === 'fulfilled') setInvites(invitesRes.value.data);
    if (auditRes.status === 'fulfilled') setAudit(auditRes.value.data);
    setLoading(false);
  }, [familyId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const sendInvite = useCallback(
    async (input: CreateEnrollmentInput): Promise<CreatedEnrollmentToken> => {
      if (!familyId)
        throw new ApiError(t('account.family.noneSelected'), 'NO_FAMILY', 400);
      const response = await api.createFamilyEnrollmentToken(familyId, input);
      await refresh();
      return response.data;
    },
    [familyId, refresh]
  );

  const revokeToken = useCallback(
    async (tokenId: string) => {
      if (!familyId) return;
      await api.revokeFamilyEnrollmentToken(familyId, tokenId);
      await refresh();
    },
    [familyId, refresh]
  );

  const regenerateToken = useCallback(
    async (tokenId: string): Promise<CreatedEnrollmentToken> => {
      if (!familyId)
        throw new ApiError(t('account.family.noneSelected'), 'NO_FAMILY', 400);
      const response = await api.regenerateFamilyEnrollmentToken(
        familyId,
        tokenId
      );
      await refresh();
      return response.data;
    },
    [familyId, refresh]
  );

  const setMemberRole = useCallback(
    async (userId: string, role: FamilyRole) => {
      if (!familyId) return;
      await api.updateFamilyMember(familyId, userId, { role });
      await refresh();
    },
    [familyId, refresh]
  );

  const removeMember = useCallback(
    async (userId: string) => {
      if (!familyId) return;
      await api.removeFamilyMember(familyId, userId);
      await refresh();
    },
    [familyId, refresh]
  );

  return {
    family,
    tokens,
    invites,
    audit,
    loading,
    error,
    failure,
    refresh,
    sendInvite,
    revokeToken,
    regenerateToken,
    setMemberRole,
    removeMember,
  };
}
